import { useEffect, useMemo, useRef, useState } from "react";

import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import YouTubeIcon from "@mui/icons-material/YouTube";
import BookmarkIcon from "@mui/icons-material/Bookmark";

import whiteLogoAsset, {
  ReactComponent as WhiteLogo,
} from "../../assets/logo/white-logo.svg";
import whiteLogoGlitchAsset from "../../assets/logo/white-logo-glitch.svg";
import categoryRunning from "../../assets/fitness-categories/corrida.webp";
import categoryStrength from "../../assets/fitness-categories/musculacao.webp";
import categoryNutrition from "../../assets/fitness-categories/nutricao.webp";
import categoryPilates from "../../assets/fitness-categories/pilates.webp";
import categoryYoga from "../../assets/fitness-categories/yoga.webp";
import systemLesson from "../../assets/mockups/system-lesson.png";
import webChat from "../../assets/mockups/web/Chat · Dark · Editable · 1280×720.png";
import webCheckout from "../../assets/mockups/web/Checkout · Dark · Editable · 1280×720.png";
import webCommunityGroup from "../../assets/mockups/web/Community Group · Dark · Editable · 1280×720.png";
import webCommunityHome from "../../assets/mockups/web/Community Home · Dark · Editable · 1280×720.png";
import webContent from "../../assets/mockups/web/Content · WellFit · Dark · Editable · 1280×720.png";
import webInsights from "../../assets/mockups/web/Insights · Dark · Editable · 1280×720.png";
import webSectionHome from "../../assets/mockups/web/Section Home · Dark · Editable · 1280×720.png";
import flagAustralia from "../../assets/flags/au.svg";
import flagCanada from "../../assets/flags/ca.svg";
import flagSpain from "../../assets/flags/es.svg";
import flagUnitedKingdom from "../../assets/flags/gb.svg";
import flagMexico from "../../assets/flags/mx.svg";
import flagPortugal from "../../assets/flags/pt.svg";
import flagUnitedStates from "../../assets/flags/us.svg";
import avatarOne from "../../assets/personas/avatars/avatar (1).png";
import avatarTwo from "../../assets/personas/avatars/avatar (2).png";
import avatarThree from "../../assets/personas/avatars/avatar (3).png";
import GlobalGlobe from "../influencers-home/components/GlobalGlobe";
import {
  appendAttributionToUrl,
  attributionEventParams,
  captureAttribution,
} from "./attribution";
import { initFacebookPixel, trackPixelEvent } from "./facebookPixel";
import "../influencers-home/styles.css";
import "./styles.css";

const FORM_URL = "https://ginastee67342.activehosted.com/f/1";
const APPLICATION_SECTION_ID = "candidatura";
const PAGE_TITLE = "Ginastee | Aplique para criar sua comunidade fitness";
const PAGE_DESCRIPTION =
  "Candidature-se ao Ginastee: creators de fitness e wellness com audiência consolidada transformam método em produto, seguidores em membros e influência em receita recorrente.";
const manifestoAvatars = [avatarOne, avatarTwo, avatarThree];

const platformScreens = [
  {
    id: "community",
    label: "Comunidade",
    image: webCommunityHome,
    alt: "Página inicial de uma comunidade fitness personalizada no Ginastee",
  },
  {
    id: "groups",
    label: "Grupos",
    image: webCommunityGroup,
    alt: "Feed, ranking e planos de membros de um grupo no Ginastee",
  },
  {
    id: "content",
    label: "Conteúdos",
    image: webContent,
    alt: "Aula em vídeo e playlist de conteúdos dentro do Ginastee",
  },
  {
    id: "journeys",
    label: "Trilhas",
    image: webSectionHome,
    alt: "Trilha de treinamento com playlists e progresso no Ginastee",
  },
  {
    id: "chat",
    label: "Chat",
    image: webChat,
    alt: "Conversas diretas entre criadores e membros no Ginastee",
  },
  {
    id: "checkout",
    label: "Checkout",
    image: webCheckout,
    alt: "Checkout personalizado para assinatura de uma comunidade no Ginastee",
  },
  {
    id: "insights",
    label: "Insights",
    image: webInsights,
    alt: "Painel de insights com vendas, receita e novos membros no Ginastee",
  },
];

const features = [
  {
    number: "01",
    title: "Experiência personalizável",
    text: "Sua identidade, seus produtos e sua linguagem em um espaço reconhecível dentro do Ginastee.",
  },
  {
    number: "02",
    title: "Fitness first",
    text: "Treinos, aulas, programas, desafios e comunidade pensados para fitness e wellness.",
  },
  {
    number: "03",
    title: "Receita recorrente",
    text: "Assinaturas e produtos premium conectados à experiência do membro, sem mensalidade para começar.",
  },
  {
    number: "04",
    title: "Venda global",
    text: "Pagamentos seguros para levar seu método aos mercados e moedas suportados pela operação.",
  },
];

const applicationSteps = [
  {
    number: "01",
    title: "Você preenche o formulário",
    text: "Conte quem você é, onde está sua audiência e qual método você quer transformar em produto.",
  },
  {
    number: "02",
    title: "Analisamos seu perfil",
    text: "Avaliamos aderência ao nicho de fitness e wellness, engajamento da audiência e potencial da oferta.",
  },
  {
    number: "03",
    title: "Conversamos com você",
    text: "Se houver aderência, nosso time entra em contato pelo canal informado para entender seus objetivos.",
  },
  {
    number: "04",
    title: "Sua comunidade sai do papel",
    text: "Estruturamos juntos sua oferta, seus conteúdos e seu checkout para você começar a vender.",
  },
];

const applicationProfile = [
  "Produz conteúdo de fitness ou wellness e tem audiência engajada",
  "Tem um método, treino ou acompanhamento que pode virar produto digital",
  "Recebe pedidos por programas, aulas ou conteúdos exclusivos",
  "Quer depender menos de publis e construir receita recorrente própria",
];

const faqs = [
  {
    question: "Como funciona a candidatura?",
    answer:
      "Você preenche o formulário de aplicação, nosso time analisa seu perfil e, havendo aderência, entramos em contato pelo canal que você informar para dar sequência.",
  },
  {
    question: "Preciso pagar para me candidatar?",
    answer:
      "Não. A candidatura é gratuita e não há mensalidade, taxa de configuração ou custo de onboarding para estruturar seu espaço. A Ginastee ganha quando você vende.",
  },
  {
    question: "Preciso de quantos seguidores para aplicar?",
    answer:
      "Não trabalhamos com um número fixo. Procuramos sinais de audiência consolidada, engajamento real e um método que possa virar produto dentro de fitness e wellness.",
  },
  {
    question: "Vou ter um aplicativo com a minha marca?",
    answer:
      "Sua comunidade vive no aplicativo e na experiência web do Ginastee. Ela não é white-label, mas é altamente personalizável com sua identidade, produtos, conteúdos e linguagem.",
  },
  {
    question: "Posso vender para outros países e receber em dólar?",
    answer:
      "Sim. A estrutura de pagamentos usa Stripe e permite vender nos mercados e moedas suportados pela operação. Disponibilidade, conversão e repasse podem variar conforme o país e a conta conectada.",
  },
  {
    question: "O que posso vender depois de aprovado?",
    answer:
      "Assinaturas, programas, treinos, aulas em vídeo, desafios e conteúdos premium para fitness e wellness.",
  },
];

const manifesto =
  "Seguidores são apenas o começo. O Ginastee transforma atenção em receita recorrente sem fronteiras.";

const heroCategories = [
  {
    className: "tile-one",
    title: "Yoga",
    image: categoryYoga,
  },
  {
    className: "tile-two",
    title: "Pilates",
    image: categoryPilates,
  },
  {
    className: "tile-three",
    title: "Corrida",
    image: categoryRunning,
  },
  {
    className: "tile-four",
    title: "Musculação",
    image: categoryStrength,
  },
  {
    className: "tile-five",
    title: "Nutrição",
    image: categoryNutrition,
  },
];

const countryMarkets = [
  { flag: flagUnitedStates, name: "Estados Unidos" },
  { flag: flagPortugal, name: "Portugal" },
  { flag: flagUnitedKingdom, name: "Reino Unido" },
  { flag: flagMexico, name: "México" },
  { flag: flagCanada, name: "Canadá" },
  { flag: flagAustralia, name: "Austrália" },
  { flag: flagSpain, name: "Espanha" },
];

const audienceOptions = [10000, 25000, 50000, 100000, 250000, 500000];

function clamp(value, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value));
}

function formatCurrency(value, currency) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

const PAGE_URL = "https://ginastee.com/apply";

const metaOverrides = [
  { selector: 'meta[name="description"]', attribute: "content", value: PAGE_DESCRIPTION },
  { selector: 'meta[property="og:title"]', attribute: "content", value: PAGE_TITLE },
  {
    selector: 'meta[property="og:description"]',
    attribute: "content",
    value: PAGE_DESCRIPTION,
  },
  { selector: 'meta[property="og:url"]', attribute: "content", value: PAGE_URL },
  { selector: 'meta[name="twitter:title"]', attribute: "content", value: PAGE_TITLE },
  {
    selector: 'meta[name="twitter:description"]',
    attribute: "content",
    value: PAGE_DESCRIPTION,
  },
  { selector: 'link[rel="canonical"]', attribute: "href", value: PAGE_URL },
];

/* Mantém o CTA flutuante no rodapé enquanto a seção da candidatura está na tela.
   Ele se esconde quando o CTA final da própria seção aparece, para não duplicar. */
function useStickyApplicationCta() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const section = document.getElementById(APPLICATION_SECTION_ID);
    if (!section) return undefined;

    const anchor = document.querySelector("[data-application-cta-anchor]");
    const state = { section: false, anchor: false };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === section) state.section = entry.isIntersecting;
          if (entry.target === anchor) state.anchor = entry.isIntersecting;
        });
        setIsVisible(state.section && !state.anchor);
      },
      { threshold: 0 },
    );

    observer.observe(section);
    if (anchor) observer.observe(anchor);

    return () => observer.disconnect();
  }, []);

  return isVisible;
}

function usePageMetadata() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    const restore = metaOverrides.map(({ selector, attribute, value }) => {
      const tag = document.querySelector(selector);
      const previous = tag?.getAttribute(attribute);
      tag?.setAttribute(attribute, value);
      return { tag, attribute, previous };
    });

    return () => {
      document.title = previousTitle;
      restore.forEach(({ tag, attribute, previous }) => {
        if (tag && previous != null) tag.setAttribute(attribute, previous);
      });
    };
  }, []);
}

function useScrollExperience() {
  useEffect(() => {
    const main = document.querySelector("main.creator-home");
    const root = document.querySelector(".creator-home");
    const progressLabel = document.querySelector(".header-progress-value");
    const revealElements = document.querySelectorAll(".creator-home .reveal");
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (reduceMotion) {
      revealElements.forEach((element) => element.classList.add("is-visible"));
    }

    const observer = reduceMotion
      ? null
      : new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add("is-visible");
                observer.unobserve(entry.target);
              }
            });
          },
          { threshold: 0.15, rootMargin: "0px 0px -6%" },
        );

    if (observer)
      revealElements.forEach((element) => observer.observe(element));

    const update = () => {
      if (!main || !root) return;
      const viewport = main.clientHeight;
      const maxScroll = main.scrollHeight - viewport;
      const pageProgress = maxScroll > 0 ? main.scrollTop / maxScroll : 0;
      root.style.setProperty("--page-progress", pageProgress);
      if (progressLabel)
        progressLabel.textContent = `${Math.round(pageProgress * 100)}%`;

      const productSection = document.querySelector(".product-film-section");
      if (productSection) {
        const rect = productSection.getBoundingClientRect();
        const distance = Math.max(rect.height - viewport, 1);
        const productProgress = clamp(-rect.top / distance);
        root.style.setProperty("--product-progress", productProgress);
      }

      const manifestoSection = document.querySelector(".manifesto-section");
      if (manifestoSection) {
        const rect = manifestoSection.getBoundingClientRect();
        const manifestoProgress = clamp(
          (viewport * 0.78 - rect.top) / (rect.height * 0.56),
        );
        root.style.setProperty("--manifesto-progress", manifestoProgress);
      }
    };

    main?.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true });
    update();

    return () => {
      observer?.disconnect();
      main?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);
}

function ActionLink({
  children,
  variant = "light",
  className = "",
  icon: Icon = ArrowOutwardRoundedIcon,
  ...props
}) {
  return (
    <a className={`action-link action-${variant} ${className}`} {...props}>
      <span>{children}</span>
      <Icon aria-hidden="true" />
    </a>
  );
}

/* CTA final: leva direto ao formulário de aplicação.
   O formulário está em outro domínio, então registramos o clique no Pixel e
   levamos os parâmetros de campanha junto na URL. */
function ApplyLink({ children, className = "", location = "page", href }) {
  return (
    <ActionLink
      href={href || FORM_URL}
      className={className}
      onClick={() =>
        trackPixelEvent("ApplyCtaClick", {
          location,
          label: children,
          ...attributionEventParams(),
        })
      }
    >
      {children}
    </ActionLink>
  );
}

/* CTA das seções acima de "Como funciona a candidatura": rola suavemente até lá
   (o scroll suave vem de `scroll-behavior: smooth` no container .creator-home). */
function ScrollToApplicationLink({ children, className = "", onClick }) {
  return (
    <ActionLink
      href={`#${APPLICATION_SECTION_ID}`}
      className={className}
      icon={ArrowForwardRoundedIcon}
      onClick={onClick}
    >
      {children}
    </ActionLink>
  );
}

function PlatformShowcase() {
  const [activeScreen, setActiveScreen] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const tabRefs = useRef([]);

  useEffect(() => {
    if (
      isPaused ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return undefined;
    }

    const advanceTimeout = window.setTimeout(() => {
      setActiveScreen((current) => (current + 1) % platformScreens.length);
    }, 3100);

    return () => window.clearTimeout(advanceTimeout);
  }, [activeScreen, isPaused]);

  const moveToScreen = (index, focusTab = false) => {
    const nextIndex = (index + platformScreens.length) % platformScreens.length;
    setActiveScreen(nextIndex);
    if (focusTab) tabRefs.current[nextIndex]?.focus();
  };

  const handleTabKeyDown = (event, index) => {
    const keyDirections = {
      ArrowLeft: index - 1,
      ArrowRight: index + 1,
      Home: 0,
      End: platformScreens.length - 1,
    };

    if (keyDirections[event.key] === undefined) return;
    event.preventDefault();
    moveToScreen(keyDirections[event.key], true);
  };

  return (
    <section
      className={`platform-showcase ${isPaused ? "is-paused" : ""}`}
      id="produto"
      aria-labelledby="platform-showcase-title"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setIsPaused(false);
        }
      }}
    >
      <div className="platform-showcase-glow" aria-hidden="true" />
      <div className="platform-showcase-shell page-shell">
        <header className="platform-showcase-heading reveal">
          <span>O que você recebe ao ser aprovado</span>
          <h2 id="platform-showcase-title">
            O Ginastee reúne tudo que você precisa para escalar no nicho fitness.
          </h2>
          <p>
            Você não precisa de mais uma ferramenta. Precisa de uma plataforma
            para transformar seu método em um negócio.
          </p>
        </header>

        <div
          className="platform-showcase-tabs reveal"
          role="tablist"
          aria-label="Recursos da plataforma Ginastee"
        >
          {platformScreens.map((screen, index) => {
            const isActive = index === activeScreen;
            return (
              <button
                type="button"
                className={`platform-showcase-tab ${isActive ? "is-active" : ""}`}
                role="tab"
                id={`platform-tab-${screen.id}`}
                aria-controls={`platform-panel-${screen.id}`}
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                onClick={() => moveToScreen(index)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
                ref={(element) => {
                  tabRefs.current[index] = element;
                }}
                key={screen.id}
              >
                <span>{screen.label}</span>
                {isActive && (
                  <i
                    className="platform-showcase-tab-progress"
                    key={`${screen.id}-${activeScreen}`}
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>

        <div className="platform-showcase-stage">
          <div className="platform-showcase-logo-art" aria-hidden="true">
            <img src={whiteLogoGlitchAsset} alt="" />
          </div>

          <div className="platform-showcase-frame">
            <div className="platform-showcase-viewport">
              {platformScreens.map((screen, index) => {
                const isActive = index === activeScreen;
                return (
                  <div
                    className={`platform-showcase-panel ${isActive ? "is-active" : ""}`}
                    role="tabpanel"
                    id={`platform-panel-${screen.id}`}
                    aria-labelledby={`platform-tab-${screen.id}`}
                    aria-hidden={!isActive}
                    key={screen.id}
                  >
                    <img
                      src={screen.image}
                      alt={screen.alt}
                      loading={index === 0 ? "eager" : "lazy"}
                      draggable="false"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ApplyHeader() {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const main = document.querySelector("main.creator-home");
    const closeOnOutsideClick = (event) => {
      if (!menuRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnScroll = () => setOpen(false);

    document.addEventListener("pointerdown", closeOnOutsideClick);
    main?.addEventListener("scroll", closeOnScroll, { passive: true });

    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      main?.removeEventListener("scroll", closeOnScroll);
    };
  }, [open]);

  return (
    <header className="benchmark-header">
      <div className="header-gradient-blur" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>
      <div className="header-left">
        <a className="header-mark" href="#top" aria-label="Ginastee, início">
          <WhiteLogo className="header-logo" aria-hidden="true" />
        </a>
      </div>

      <div
        className={open ? "header-menu is-open" : "header-menu"}
        ref={menuRef}
      >
        <button
          type="button"
          className="header-progress"
          aria-label={open ? "Fechar menu" : "Abrir menu"}
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <CloseRoundedIcon /> : <MenuRoundedIcon />}
          <span>{open ? "Fechar" : "Menu"}</span>
          <b className="header-progress-value">0%</b>
        </button>

        <div className="header-menu-panel" aria-hidden={!open}>
          <span className="menu-group-label">Menu</span>
          <nav aria-label="Menu expandido">
            <a href="#produto" onClick={() => setOpen(false)}>
              A plataforma
            </a>
            <a href="#candidatura" onClick={() => setOpen(false)}>
              Como funciona a candidatura
            </a>
            <a href="#global" onClick={() => setOpen(false)}>
              Alcance global
            </a>
            <a href="#precos" onClick={() => setOpen(false)}>
              Preços
            </a>
          </nav>
          <div className="menu-divider" />
          <span className="menu-group-label">Social</span>
          <div className="menu-social-links">
            <a href="https://www.instagram.com/ginastee.app/">Instagram</a>
            <a href="https://www.youtube.com/@ginasteebr">YouTube</a>
            <a href="https://www.linkedin.com/company/ginastee/">LinkedIn</a>
          </div>
          <ScrollToApplicationLink
            className="header-menu-cta"
            onClick={() => setOpen(false)}
          >
            Aplicar agora
          </ScrollToApplicationLink>
        </div>
      </div>

      <div className="header-actions">
        <ScrollToApplicationLink>Aplicar agora</ScrollToApplicationLink>
      </div>
    </header>
  );
}

function RevenueCalculator() {
  const [audienceIndex, setAudienceIndex] = useState(2);
  const [conversion, setConversion] = useState(0.5);
  const [price, setPrice] = useState(39);
  const [currency, setCurrency] = useState("USD");

  const result = useMemo(() => {
    const members = Math.round(
      audienceOptions[audienceIndex] * (conversion / 100),
    );
    return { members, monthly: members * price };
  }, [audienceIndex, conversion, price]);

  return (
    <div className="calculator-panel">
      <div className="calculator-controls">
        <div className="calculator-control">
          <div>
            <label htmlFor="audience">Seguidores</label>
            <strong>
              {audienceOptions[audienceIndex].toLocaleString("pt-BR")}
            </strong>
          </div>
          <input
            id="audience"
            type="range"
            min="0"
            max={audienceOptions.length - 1}
            step="1"
            value={audienceIndex}
            onChange={(event) => setAudienceIndex(Number(event.target.value))}
          />
        </div>
        <div className="calculator-control">
          <div>
            <label htmlFor="conversion">Conversão</label>
            <strong>{conversion.toFixed(1).replace(".", ",")}%</strong>
          </div>
          <input
            id="conversion"
            type="range"
            min="0.1"
            max="2"
            step="0.1"
            value={conversion}
            onChange={(event) => setConversion(Number(event.target.value))}
          />
        </div>
        <div className="calculator-control">
          <div>
            <label htmlFor="price">Preço mensal</label>
            <span className="currency-toggle" role="group" aria-label="Moeda">
              {["USD", "BRL"].map((value) => (
                <button
                  type="button"
                  className={currency === value ? "is-active" : ""}
                  onClick={() => setCurrency(value)}
                  key={value}
                >
                  {value}
                </button>
              ))}
            </span>
          </div>
          <div className="calculator-price">
            <span>{currency === "USD" ? "US$" : "R$"}</span>
            <input
              id="price"
              type="number"
              min="1"
              value={price}
              onChange={(event) =>
                setPrice(Math.max(1, Number(event.target.value)))
              }
            />
          </div>
        </div>
      </div>
      <div className="calculator-output" aria-live="polite">
        <span>Potencial mensal</span>
        <strong>{formatCurrency(result.monthly, currency)}</strong>
        <p>{result.members} membros pagantes</p>
        <div>
          <span>Potencial em 12 meses</span>
          <b>{formatCurrency(result.monthly * 12, currency)}</b>
        </div>
      </div>
    </div>
  );
}

function ApplyFooter() {
  return (
    <footer className="benchmark-footer">
      <div className="footer-brand">
        <img src={whiteLogoAsset} alt="Ginastee" />
        <p>Fitness creators, global communities, recurring revenue.</p>
      </div>
      <div className="footer-columns">
        <div>
          <strong>Candidatura</strong>
          <a href="#produto">A plataforma</a>
          <a href="#candidatura">Como funciona</a>
          <a href="#precos">Preços</a>
        </div>
        <div>
          <strong>Ginastee</strong>
          <a href="#global">Alcance global</a>
          <a href="#faq">Perguntas frequentes</a>
          <a href="/privacy-policy">Privacidade</a>
        </div>
        <div>
          <strong>Social</strong>
          <a href="https://www.instagram.com/ginastee.app/">
            <InstagramIcon /> Instagram
          </a>
          <a href="https://www.youtube.com/@ginasteebr">
            <YouTubeIcon /> YouTube
          </a>
          <a href="https://www.linkedin.com/company/ginastee/">
            <LinkedInIcon /> LinkedIn
          </a>
        </div>
      </div>
      <div className="footer-bottom">
        <span>© {new Date().getFullYear()} Ginastee</span>
        <span>Feito no Brasil para criadores sem fronteiras.</span>
      </div>
    </footer>
  );
}

export default function Apply() {
  const isStickyCtaVisible = useStickyApplicationCta();
  usePageMetadata();
  useScrollExperience();

  /* Guarda fbclid/utm da URL para reaproveitar no formulário e no Lead. */
  const formUrl = useMemo(
    () => appendAttributionToUrl(FORM_URL, captureAttribution()),
    [],
  );

  useEffect(() => {
    initFacebookPixel();
  }, []);

  return (
    <main className="creator-home apply-page" id="top">
      <ApplyHeader />

      <section className="benchmark-hero">
        <video
          className="hero-video"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
        >
          <source src="/media/home-yoga.mp4" type="video/mp4" />
        </video>
        <div className="hero-video-overlay" />
        {heroCategories.map((category) => (
          <figure
            className={`hero-category-card ${category.className}`}
            key={category.className}
          >
            <img
              src={category.image}
              alt={`${category.title}: categoria fitness`}
            />
            <span className="hero-category-favorite" aria-hidden="true">
              <BookmarkIcon />
            </span>
            <figcaption>{category.title}</figcaption>
          </figure>
        ))}

        <div className="hero-center reveal is-visible">
          <span>Candidaturas abertas para creators de fitness e wellness</span>
          <h1>Aplique e transforme seguidores em receita recorrente global.</h1>
          <p>
            Selecionamos creators com audiência consolidada para lançar a
            própria comunidade no Ginastee. Preencha o formulário e nosso time
            avalia seu perfil.
          </p>
          <div className="hero-actions">
            <ScrollToApplicationLink>Aplicar agora</ScrollToApplicationLink>
            <a href="#produto" className="text-link">
              Ver a plataforma <ArrowForwardRoundedIcon />
            </a>
          </div>
        </div>
        <a href="#produto" className="hero-scroll">
          Scroll para explorar <span />
        </a>
      </section>

      <PlatformShowcase />

      <section className="product-film-section" id="produto-em-acao">
        <div className="product-film-sticky">
          <div className="product-film-frame">
            <div className="film-frame-header">
              <span>Ginastee product preview</span>
              <span>Community lesson</span>
            </div>
            <div className="product-lesson-preview">
              <img
                className="product-lesson-image"
                src={systemLesson}
                alt="Aula em vídeo dentro de uma comunidade Ginastee, com playlist e comentários"
              />
              <div className="screen-specification product-lesson-copy">
                <span>Conteúdos exclusivos</span>
                <strong>Seu método em um ambiente criado por você!</strong>
                <small>Vídeo, playlists e interação em um só lugar</small>
                <p>
                  Entregue aulas em vídeo com materiais de apoio, comentários,
                  avaliações e uma playlist que conduz o membro para o próximo
                  conteúdo.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="manifesto-section">
        <p aria-label={manifesto}>
          {manifesto.split(" ").map((word, index) => (
            <span className="manifesto-token" key={`${word}-${index}`}>
              <span
                className="manifesto-word"
                style={{ "--word-index": index }}
                aria-hidden="true"
              >
                {word === "membros," ? "membros" : `${word} `}
              </span>
              {word === "membros," && (
                <>
                  <span
                    className="manifesto-avatars"
                    style={{ "--word-index": index }}
                    aria-hidden="true"
                  >
                    {manifestoAvatars.map((avatar, avatarIndex) => (
                      <img
                        src={avatar}
                        alt=""
                        key={avatar}
                        style={{ "--avatar-index": avatarIndex }}
                      />
                    ))}
                  </span>
                  <span
                    className="manifesto-word"
                    style={{ "--word-index": index }}
                    aria-hidden="true"
                  >
                    ,{" "}
                  </span>
                </>
              )}
            </span>
          ))}
        </p>
      </section>

      <section className="standard-section page-shell">
        <div className="section-intro reveal">
          <span className="micro-label">
            Construído para transformar influência em negócio
          </span>
          <h2>O feed chama atenção. O produto cria valor.</h2>
          <p>
            Ao ser aprovado, você recebe a estrutura que conecta tudo que
            acontece depois do post: entrega, comunidade, retenção e pagamento.
          </p>
        </div>
        <div className="feature-rows reveal">
          {features.map((feature) => (
            <article key={feature.number}>
              <span>{feature.number}</span>
              <h3>{feature.title}</h3>
              <p>{feature.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="global-section" id="global">
        <div className="global-layout">
          <div className="global-visual reveal">
            <GlobalGlobe />
          </div>
          <div className="global-content reveal">
            <span className="micro-label">Alcance global</span>
            <h2>Sua audiência já é global. Sua receita ainda não.</h2>
            <p>
              Venda em dólar para diversos países, apresente sua oferta em
              moedas internacionais e construa um negócio sem fronteiras.
            </p>
            <div className="country-market-row" aria-label="Países disponíveis">
              <div className="country-market-scroll">
                {countryMarkets.map((country) => (
                  <span className="country-pill" key={country.name}>
                    <img className="country-flag" src={country.flag} alt="" />
                    {country.name}
                  </span>
                ))}
              </div>
              <span className="country-count">+45 países</span>
            </div>
            <div className="global-stat">
              <span>Economia wellness global em 2024</span>
              <strong>US$ 6,8 tri</strong>
              <a
                href="https://globalwellnessinstitute.org/press-room/statistics-and-facts/"
                target="_blank"
                rel="noreferrer"
              >
                Global Wellness Institute <ArrowOutwardRoundedIcon />
              </a>
            </div>
            <div className="apply-cta-row">
              <ScrollToApplicationLink>
                Levar meu método para o mundo
              </ScrollToApplicationLink>
            </div>
          </div>
        </div>
      </section>

      <section className="calculator-section page-shell">
        <div className="section-intro reveal">
          <span className="micro-label">Faça as contas</span>
          <h2>Aqui, suas redes sociais se tornam receita recorrente</h2>
          <p>
            Antes de aplicar, simule o potencial de faturamento estimado com
            base no tamanho da sua audiência
          </p>
        </div>
        <div className="reveal">
          <RevenueCalculator />
        </div>
        <small className="calculator-note">
          Simulação ilustrativa. Resultados dependem da oferta, conversão,
          retenção, taxas e condições de pagamento.
        </small>
      </section>

      <section className="standard-section page-shell" id="candidatura">
        <div className="section-intro reveal">
          <span className="micro-label">Como funciona a candidatura</span>
          <h2>Quatro passos entre o formulário e a sua comunidade.</h2>
          <p>
            A aplicação é gratuita e leva poucos minutos. A partir dela, nosso
            time entende seu momento e o potencial da sua audiência.
          </p>
        </div>
        <div className="feature-rows reveal">
          {applicationSteps.map((step) => (
            <article key={step.number}>
              <span>{step.number}</span>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </article>
          ))}
        </div>
        <div
          className={`apply-sticky-cta ${isStickyCtaVisible ? "is-visible" : ""}`}
          aria-hidden={!isStickyCtaVisible}
        >
          <ApplyLink location="sticky-cta" href={formUrl}>
            Começar minha candidatura
          </ApplyLink>
        </div>
        <div className="section-intro apply-profile-intro reveal">
          <span className="micro-label">Quem procuramos</span>
          <h2>Você provavelmente tem aderência se…</h2>
          <ul className="apply-profile-list">
            {applicationProfile.map((item) => (
              <li key={item}>
                <CheckRoundedIcon /> {item}
              </li>
            ))}
          </ul>
          <div className="apply-cta-row" data-application-cta-anchor>
            <ApplyLink location="quem-procuramos" href={formUrl}>
              Aplicar para uma vaga
            </ApplyLink>
          </div>
        </div>
      </section>

      <section className="pricing-section page-shell" id="precos">
        <div className="pricing-copy reveal">
          <span className="micro-label">Crescemos juntos</span>
          <h2>Lucre com a sua plataforma e só pague quando vender</h2>
          <p>
            Aplicar é gratuito. Sem mensalidade, taxa de configuração ou custo
            de onboarding.
          </p>
        </div>
        <article className="pricing-card reveal">
          <div className="pricing-card-top">
            <span>Plano creator</span>
            <b>Sem mensalidade</b>
          </div>
          <p>Você paga por venda</p>
          <div className="pricing-rate">
            <strong>9,49%</strong>
            <span>por transação</span>
          </div>
          <div className="pricing-lines">
            <p>
              <span>Brasil</span>
              <b>+ R$ 1,99</b>
            </p>
            <p>
              <span>Exterior</span>
              <b>+ US$ 1,99 + Taxas Stripe</b>
            </p>
          </div>
          <ul>
            <li>
              <CheckRoundedIcon /> App e web
            </li>
            <li>
              <CheckRoundedIcon /> Conteúdo ilimitado
            </li>
            <li>
              <CheckRoundedIcon /> Pagamentos seguros
            </li>
          </ul>
          <ApplyLink location="precos" href={formUrl}>
            Quero me candidatar
          </ApplyLink>
          <small>
            Taxas de processamento, câmbio e disponibilidade variam conforme
            país, moeda e condições da Stripe.
          </small>
        </article>
      </section>

      <section className="faq-section page-shell" id="faq">
        <div className="faq-copy reveal">
          <h2>Perguntas diretas, respostas claras.</h2>
          <p>
            Ficou alguma dúvida? Registre no formulário de aplicação: nosso time
            responde junto com o retorno da sua candidatura.
          </p>
        </div>
        <div className="faq-list reveal">
          {faqs.map((faq, index) => (
            <details key={faq.question} open={index === 0}>
              <summary>
                <strong>{faq.question}</strong>
                <span>+</span>
              </summary>
              <p>{faq.answer}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="closing-section">
        <div className="closing-copy reveal">
          <span className="micro-label">Seu próximo movimento</span>
          <h2>Sua audiência já está pronta para ir além do feed!!!</h2>
          <p>
            Envie sua candidatura e comece a transformar seguidores em membros,
            seu método em produto e sua influência em receita global.
          </p>
          <div>
            <ApplyLink location="fechamento" href={formUrl}>
              Enviar minha candidatura
            </ApplyLink>
          </div>
        </div>
      </section>

      <ApplyFooter />
    </main>
  );
}
